import { Badge, Icon } from '@postscript/components';
import { ComponentProps } from 'react';
import styled, { css } from 'styled-components';

interface BadgeProps extends ComponentProps<typeof Badge> {
  style?: React.CSSProperties;
}

interface SidebarLauncherButtonProps {
  icon: () => JSX.Element;
  label: string;
  isPanelOpen: boolean;
  hasActiveState?: boolean;
  badge?: BadgeProps;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  'data-cy'?: string;
  'aria-label'?: string;
  children?: React.ReactNode;
}

const defaultState = css`
  background: var(--main-bkg-color);
  border-color: var(--border-color-dim);
  color: var(--text-color-dim);

  &:hover {
    background: var(--surface-bkg-color);
    border-color: var(--link-color-hover);
    color: var(--text-color);
  }

  &:active {
    background: var(--surface-bkg-color);
    border-color: var(--link-color-active);
    color: var(--text-color);
  }
`;

const activeState = css`
  background: var(--surface-bkg-color);
  border-color: var(--border-color-extra-dim);
  color: var(--text-color-dim);

  &:hover {
    border-color: var(--link-color-hover);
  }

  &:active {
    border-color: var(--link-color-active);
  }
`;

const panelOpenState = css`
  background: var(--surface-bkg-color);
  border-color: var(--border-color-dim);
  color: var(--text-color);

  &:hover {
    border-color: var(--link-color-hover);
  }

  &:active {
    border-color: var(--link-color-active);
  }
`;

interface StyledButtonProps {
  $isPanelOpen: boolean;
  $hasActiveState: boolean;
}

const StyledButton = styled.button<StyledButtonProps>`
  display: flex;
  align-items: center;
  gap: var(--spacing-1);
  flex: 1;
  min-width: 0;
  padding: var(--spacing-2);
  border: 1px solid;
  border-radius: var(--border-radius-small);
  font: var(--body-text-small);
  font-weight: var(--body-text-bold-font-weight);
  cursor: pointer;
  overflow: hidden;
  transition: border-color var(--hover-transition),
    background-color var(--hover-transition), color var(--hover-transition);

  ${({ $isPanelOpen, $hasActiveState }) => {
    if ($isPanelOpen) return panelOpenState;
    if ($hasActiveState) return activeState;
    return defaultState;
  }}

  &:focus-visible {
    outline: 2px solid var(--highlight-color);
    outline-offset: 2px;
  }
`;

const StyledLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledBadgeWrapper = styled.span`
  margin-left: auto;
`;

const SidebarLauncherButton: React.FC<SidebarLauncherButtonProps> = ({
  icon,
  label,
  isPanelOpen,
  hasActiveState = false,
  badge,
  onClick,
  children,
  ...rest
}) => {
  return (
    <StyledButton
      $isPanelOpen={isPanelOpen}
      $hasActiveState={hasActiveState}
      onClick={onClick}
      {...rest}
    >
      <Icon component={icon} size={24} />
      <StyledLabel>{label}</StyledLabel>
      {badge && (
        <StyledBadgeWrapper>
          <Badge {...badge} />
        </StyledBadgeWrapper>
      )}
      {children}
    </StyledButton>
  );
};

export default SidebarLauncherButton;
